import React, { useEffect, useState } from "react";
import { Toaster, useToastController } from "@fluentui/react-toast";
import toastEmitter from "../../utils/toastEmitter";
import configManagerEmitter from "../../utils/configManagerEmitter";
import configManager from "../../singleton/ConfigManager";
import PropTypes from "prop-types";
import { makeStyles, mergeClasses } from "@fluentui/react-components";

const toasterId = "notifications-center-toaster";
const TOAST_LIMIT = 2;
const DEFAULT_POSITION = "top";
const DEBUG_NOTIFICATIONS =
  typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug_notifications") === "1";

const useStyles = makeStyles({
  root: {
    zIndex: 2147483647,
    // Override the motion system's display:none on ToastContainer.
    // The enter animation fails on React 16 because the element starts hidden,
    // giving it scrollHeight=0, making the animation a no-op, so onMotionFinish
    // never fires and the container stays hidden permanently.
    "& .fui-ToastContainer": {
      display: "block !important"
    }
  },
  loading: {
    display: "none"
  }
});

export const NotificationsCenterToaster = ({ loading }) => {
  const { dispatchToast, dismissToast, updateToast } = useToastController(toasterId);
  const classes = useStyles();

  const [position, setPosition] = useState(DEFAULT_POSITION);
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    const handleConfigUpdated = config => {
      if (DEBUG_NOTIFICATIONS) {
        console.debug("[notifications][toaster] configUpdated", {
          enabled: config?.notifications?.enabled,
          position: config?.notifications?.position
        });
      }
      setIsEnabled(config?.notifications?.enabled ?? true);
      setPosition(config.notifications?.position ?? DEFAULT_POSITION);
    };

    // Initialize from current config in case this component subscribes after initial emit.
    handleConfigUpdated(configManager.getConfig());

    configManagerEmitter.on("configUpdated", handleConfigUpdated);

    return () => {
      configManagerEmitter.off("configUpdated", handleConfigUpdated);
    };
  }, []);

  useEffect(() => {
    const handleToast = (toastContent, options) => {
      if (!isEnabled) {
        if (DEBUG_NOTIFICATIONS) {
          console.debug("[notifications][toaster] toast dropped because disabled", { options });
        }
        return;
      }

      if (DEBUG_NOTIFICATIONS) {
        console.debug("[notifications][toaster] dispatching toast", {
          options,
          position,
          loading
        });
      }

      try {
        const toastId = dispatchToast(toastContent, {
          ...options,
          position
        });

        if (DEBUG_NOTIFICATIONS) {
          requestAnimationFrame(() => {
            const toasterCount = document.querySelectorAll(".fui-Toaster").length;
            const toastCount = document.querySelectorAll(".fui-Toast").length;
            const toastEl = document.querySelector(".fui-Toast");
            const toastComputed = toastEl ? window.getComputedStyle(toastEl) : null;
            const toastRect = toastEl ? toastEl.getBoundingClientRect() : null;
            const ancestors = [];
            let node = toastEl?.parentElement || null;
            let depth = 0;
            while (node && depth < 8) {
              const computed = window.getComputedStyle(node);
              const rect = node.getBoundingClientRect();
              ancestors.push({
                tag: node.tagName,
                className: node.className || "",
                display: computed.display,
                visibility: computed.visibility,
                opacity: computed.opacity,
                zIndex: computed.zIndex,
                transform: computed.transform,
                rect: {
                  x: rect.x,
                  y: rect.y,
                  width: rect.width,
                  height: rect.height
                }
              });
              node = node.parentElement;
              depth += 1;
            }
            console.debug("[notifications][toaster] dispatch result", {
              toastId,
              toasterCount,
              toastCount,
              toastStyle: toastComputed
                ? {
                    display: toastComputed.display,
                    visibility: toastComputed.visibility,
                    opacity: toastComputed.opacity,
                    zIndex: toastComputed.zIndex,
                    transform: toastComputed.transform
                  }
                : null,
              toastRect: toastRect
                ? {
                    x: toastRect.x,
                    y: toastRect.y,
                    width: toastRect.width,
                    height: toastRect.height
                  }
                : null,
              ancestors
            });
          });
        }
      } catch (error) {
        console.error("Failed to dispatch toast:", error);
      }
    };

    const handleDismissToast = id => {
      try {
        dismissToast(id);
      } catch (error) {
        console.error("Failed to dismiss toast:", error);
      }
    };

    const handleUpdateToast = (toastContent, options) => {
      try {
        updateToast({
          content: toastContent,
          ...options
        });
      } catch (error) {
        console.error("Failed to update toast:", error);
      }
    };

    toastEmitter.on("toast", handleToast);
    toastEmitter.on("dismissToast", handleDismissToast);
    toastEmitter.on("updateToast", handleUpdateToast);

    return () => {
      toastEmitter.off("toast", handleToast);
      toastEmitter.off("dismissToast", handleDismissToast);
      toastEmitter.off("updateToast", handleUpdateToast);
    };
  }, [dispatchToast, dismissToast, updateToast, isEnabled, position, loading]);

  useEffect(() => {
    if (DEBUG_NOTIFICATIONS) {
      console.debug("[notifications][toaster] render state", { loading, isEnabled, position });
    }
  }, [loading, isEnabled, position]);

  return (
    <Toaster
      limit={TOAST_LIMIT}
      toasterId={toasterId}
      position="top"
      className={mergeClasses(classes.root, loading ? classes.loading : null)}
    />
  );
};

NotificationsCenterToaster.propTypes = {
  loading: PropTypes.bool
};
